import { CloseOutlined } from '@ant-design/icons'
import { Button, Flex, Modal, Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { productApi } from 'api/product'
import { ISubCategory, ESubCategoryStatus } from 'models/product'
import { Key, useEffect, useState } from 'react'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'

interface Props {
  visible: boolean
  multiple?: boolean
  onClose: () => void
  onSave: (sc: ISubCategory) => void
}

export const GetSubCategoryModal = ({
  visible,
  onClose,
  onSave,
  multiple
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedSubCategories, setSelectedSubCategories] = useState<
    ISubCategory[]
  >([])
  const [subCategories, setSubCategories] = useState<ISubCategory[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 20

  const getSubCategories = async (skip: number) => {
    try {
      setIsLoading(true)
      const response = await productApi.getAllSubCategories(skip, pageSize)
      if (
        (response.data as any).subCategories &&
        (response.data as any).count
      ) {
        setSubCategories((response.data as any).subCategories)
        setTotal((response.data as any).count)
      } else {
        setSubCategories(response.data)
        // Эвристика для total, если API не возвращает общее количество
        if (response.data.length === pageSize) {
          setTotal(skip + pageSize + 1)
        } else {
          setTotal(skip + response.data.length)
        }
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const getData = async () => {
      try {
        await getSubCategories(0)
        setCurrentPage(1)
      } catch (e) {
        showServerError(e)
      }
    }
    if (visible) {
      getData()
    }
  }, [visible])

  const onPageChange = (current: number) => {
    setCurrentPage(current)
    getSubCategories((current - 1) * pageSize)
  }

  const onRowSelection = (_: Key[], selectedRows: ISubCategory[]) => {
    const selectedIds = new Set(selectedRows.map((r) => r._id))
    setSelectedSubCategories(
      selectedSubCategories
        .filter((item) => selectedIds.has(item._id))
        .concat(
          selectedRows.filter(
            (newItem) =>
              !selectedSubCategories.some(
                (existing) => existing._id === newItem._id
              )
          )
        )
    )
  }

  const chooseSubCategory = () => {
    if (!selectedSubCategories.length) {
      return showMessage('info', 'Выберите подкатегорию из таблицы', '')
    }
    if (multiple) {
      onSave(selectedSubCategories[0])
    } else {
      onSave(selectedSubCategories[0])
    }
    onClose()
  }

  const columns: ColumnsType<ISubCategory> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Категория',
      dataIndex: ['category_id', 'name'],
      key: 'category_name'
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: ESubCategoryStatus) => {
        let color = 'default'
        if (status === ESubCategoryStatus.ACTIVE) color = 'success'
        if (status === ESubCategoryStatus.MODERATION) color = 'warning'
        return (
          <Tag color={color}>
            {status === ESubCategoryStatus.MODERATION
              ? 'На модерации'
              : 'Активная'}
          </Tag>
        )
      }
    }
  ]

  return (
    <Modal
      centered
      open={visible}
      footer={null}
      closable={false}
      onCancel={onClose}
      width={1100}
    >
      <Flex
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 15
        }}
      >
        <Typography.Title level={4}>Подкатегории</Typography.Title>
        <CloseOutlined onClick={onClose} />
      </Flex>

      <div
        style={{
          marginBottom: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Button type='default' size='middle' onClick={onClose}>
          Отменить
        </Button>
        <Button
          type='primary'
          size='middle'
          disabled={!selectedSubCategories.length}
          onClick={chooseSubCategory}
        >
          Выбрать
        </Button>
      </div>

      <Table
        scroll={{
          x: 900
        }}
        columns={columns}
        dataSource={subCategories}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowSelection={{
          type: multiple ? 'checkbox' : 'radio',
          selectedRowKeys: selectedSubCategories.map((p) => p._id),
          onChange: onRowSelection
        }}
        rowClassName='tableRow'
        footer={undefined}
        pagination={{
          current: currentPage,
          onChange: onPageChange,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
    </Modal>
  )
}
